import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, resolveComponent as _resolveComponent, mergeProps as _mergeProps, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["innerHTML"];
const _hoisted_2 = {
  key: 1
};
const _hoisted_3 = ["id"];
const _hoisted_4 = ["innerHTML"];
import DOMPurify from 'dompurify';
import { ref, computed, watch, onBeforeUnmount } from 'vue';
export default /*@__PURE__*/Object.assign({
  name: 'CustomTextWithTooltip'
}, {
  __name: 'customTextWithTooltip',
  props: {
    /** 展示文案（与 content / 默认插槽二选一） */
    text: {
      type: String,
      default: ''
    },
    /** tooltip 浮层出现位置 */
    placement: {
      type: String,
      default: 'top'
    },
    /** 展示文案（兼容旧用法） */
    content: {
      type: String,
      default: ''
    },
    /** tooltip 浮层内容；不传则回落 content / text */
    tooltipContent: {
      type: String,
      default: ''
    },
    /** 默认超出一行省略 */
    lineNumber: {
      type: [Number, String],
      default: 1
    },
    /** 是否展示箭头 */
    showArrow: {
      type: Boolean,
      default: true
    },
    /** 透传给 el-tooltip 的 popper-class */
    popperClass: {
      type: String,
      default: ''
    },
    /** 为 true 时始终禁用 tip */
    disabled: {
      type: Boolean,
      default: false
    },
    /** tip 延迟出现（ms） */
    showAfter: {
      type: [Number, String],
      default: 400
    },
    /** tip 延迟隐藏（ms） */
    hideAfter: {
      type: [Number, String],
      default: 0
    },
    /** tip 主题 */
    effect: {
      type: String,
      default: 'dark'
    },
    /** 默认插槽内 span 的 class */
    class: {
      type: String,
      default: ''
    },
    /** 省略容器 class */
    textClass: {
      type: String,
      default: ''
    },
    /** 省略容器 max-width */
    maxWidth: {
      type: String,
      default: ''
    },
    /** true 时使用文本光标（不可点跳转场景） */
    textPointer: {
      type: Boolean,
      default: false
    },
    /** 是否直接开启 tooltip，不需要计算宽度是否足够 */
    enableTooltip: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  setup(__props, _ref) {
    let __expose = _ref.expose,
      __emit = _ref.emit;
    const emit = __emit;
    const props = __props;
    const tooltipRef = ref(null);
    const tooltipContentText = computed(() => {
      return props.tooltipContent || props.content || props.text;
    });
    const styleObj = computed(() => {
      const obj = {
        '-webkit-line-clamp': Number(props.lineNumber)
      };
      // 业务传入 maxWidth 时限制省略容器宽度
      if (props.maxWidth) {
        obj['max-width'] = props.maxWidth;
      }
      return obj;
    });

    /** true 表示禁用 tip；仅在文本实际溢出时为 false */
    const showTooltip = ref(true);

    /**
     * 每次悬停按当前容器尺寸重算是否溢出。
     * 必须用 currentTarget（省略容器），不能用 target（可能是内部 span），
     * 也不能只按文案缓存——列宽 resize 后溢出状态会变。
     * @param {MouseEvent} e - 鼠标进入事件
     * @returns {void}
     */
    function onMouseEnter(e) {
      // 显式禁用时不开启 tip
      if (props.disabled) return;

      // 业务要求始终展示 tip，跳过溢出检测
      if (props.enableTooltip) {
        showTooltip.value = false;
        return;
      }
      const dom = e.currentTarget;
      // 无容器时无法判断溢出
      if (!dom) return;
      const isOverflow = dom.offsetWidth < dom.scrollWidth || dom.offsetHeight < dom.scrollHeight - 2;
      // el-tooltip :disabled —— 未溢出时禁用 tip
      const disabled = !isOverflow;
      // 仅在状态变化时写入，减少无意义响应式更新
      if (showTooltip.value !== disabled) {
        showTooltip.value = disabled;
      }
    }

    /**
     * 关闭 tip，并尽量调用 el-tooltip 实例 hide，避免 teleport 孤儿气泡。
     * @returns {void}
     */
    function hideTooltip() {
      var _tooltipRef$value, _tooltipRef$value$hid;
      showTooltip.value = true;
      (_tooltipRef$value = tooltipRef.value) === null || _tooltipRef$value === void 0 || (_tooltipRef$value$hid = _tooltipRef$value.hide) === null || _tooltipRef$value$hid === void 0 || _tooltipRef$value$hid.call(_tooltipRef$value);
    }

    /**
     * 点击时先关 tip 再向外抛 click，避免树/列表重绘后气泡残留撑高文档。
     * @param {MouseEvent} e - 点击事件
     * @returns {void}
     */
    function onClick(e) {
      hideTooltip();
      emit('click', e);
    }
    watch(() => [props.text, props.content, props.tooltipContent, props.lineNumber, props.maxWidth, props.disabled], () => {
      // 文案或布局相关 props 变化后重置为禁用 tip，下次悬停再测
      hideTooltip();
    });
    onBeforeUnmount(() => {
      hideTooltip();
    });
    __expose({
      hideTooltip
    });
    return (_ctx, _cache) => {
      const _component_el_tooltip = _resolveComponent("el-tooltip");
      return _openBlock(), _createBlock(_component_el_tooltip, _mergeProps({
        ref_key: "tooltipRef",
        ref: tooltipRef
      }, _ctx.$attrs, {
        "popper-class": __props.popperClass,
        disabled: showTooltip.value,
        placement: __props.placement,
        "show-arrow": __props.showArrow,
        "show-after": Number(__props.showAfter),
        "hide-after": Number(__props.hideAfter),
        effect: __props.effect
      }), {
        content: _withCtx(() => [tooltipContentText.value ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          innerHTML: _unref(DOMPurify).sanitize(tooltipContentText.value)
        }, null, 8, _hoisted_1)) : (_openBlock(), _createElementBlock("span", _hoisted_2, [_renderSlot(_ctx.$slots, "default")]))]),
        default: _withCtx(() => [_createElementVNode("div", {
          id: !showTooltip.value ? 'text' : '',
          class: _normalizeClass(["text-ellipsis-multiple", [{
            'text-pointer': props.textPointer
          }, props.textClass]]),
          style: _normalizeStyle(styleObj.value),
          onMouseenter: _withModifiers(onMouseEnter, ["stop"]),
          onMouseleave: hideTooltip,
          onClick: onClick
        }, [_renderSlot(_ctx.$slots, "default", {}, () => [_createElementVNode("span", {
          class: _normalizeClass(props.class),
          innerHTML: _unref(DOMPurify).sanitize(__props.text || __props.content)
        }, null, 10, _hoisted_4)])], 46, _hoisted_3)]),
        _: 3
      }, 16, ["popper-class", "disabled", "placement", "show-arrow", "show-after", "hide-after", "effect"]);
    };
  }
});